import { Box, Typography, Card, CardActionArea } from "@mui/material";
import {
  avatarContainer,
  patientCardContainer,
  statusContainer,
  dotBox,
  patientInfoContainers,
  topPatientInfoContainerText,
  bottomPatientInfoContainerText,
  bottomPatientInfoContainerTextLight,
  patientInfoContainersContainer,
  patientCardInfoContainersParentContainer,
  npContainer,
  npText,
} from "./PatientCardStyles";
import UserAvatar from "@assets/svgs/avatars/user-avatar.svg";
import GreenDot from "@assets/svgs/dots/green-dot.svg";
import OrangeDot from "@assets/svgs/dots/orange-dot.svg";
import { useGetFeatureFlag } from "@hooks/useGetFeatureFlag";
import { EFeatureFlags } from "src/enums/EFeatureFlags";

export interface PatientCardProps {
  patientKey?: string | number;
  dob: string;
  chartNumber: string | null;
  patientName: string;
  totalForms: number;
  completedForms?: number;
  status?: string;
  age: number;
  np: boolean;
  avatar?: string | null;
  appointmentDate?: string | number;
  appointmentTime?: number;
  packageName?: string;
  noBorder?: boolean;
  isSelected?: boolean | undefined;
  onClick?: () => void;
  width?: number;
  height?: number;
}

export const PatientCard = ({
  patientKey,
  dob,
  chartNumber,
  patientName,
  totalForms,
  completedForms,
  status,
  age,
  np,
  avatar,
  appointmentDate,
  appointmentTime,
  packageName,
  isSelected,
  noBorder,
  onClick,
  width,
  height,
}: PatientCardProps) => {
  const { isEnabled: isFamilyFeatureEnabled } = useGetFeatureFlag(
    EFeatureFlags.FastrAppFamilyEnabled
  );

  const formattedDate = (() => {
    const [year, month, day] = dob.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  })();

  let formattedAppointment = "";
  if (isFamilyFeatureEnabled && appointmentDate != null) {
    const dateObj = new Date(Number(appointmentDate) * 1000);
    const dateStr = dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    const timeStr = dateObj.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    formattedAppointment = `${dateStr} @ ${timeStr}`;
  }

  let formattedTime = "";
  if (appointmentTime != null) {
    const timeNum = Number(appointmentTime);
    if (!Number.isNaN(timeNum) && timeNum > 0) {
      formattedTime = new Date(timeNum * 1000).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      formattedTime = String(appointmentTime);
    }
  }

  return (
    <Card key={patientKey} sx={{ width: "100%" }}>
      <CardActionArea
        onClick={onClick}
        sx={patientCardContainer(isSelected ?? false, noBorder ?? false)}
      >
        <Box sx={avatarContainer(width, height)}>
          <img
            src={avatar || UserAvatar}
            alt="User avatar"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = UserAvatar;
            }}
          />
        </Box>
        <Box sx={patientCardInfoContainersParentContainer}>
          <Box sx={patientInfoContainersContainer}>
            <Box sx={patientInfoContainers}>
              <Typography sx={topPatientInfoContainerText}>
                {patientName}
              </Typography>
              {isFamilyFeatureEnabled
                ? formattedAppointment && (
                    <Typography sx={topPatientInfoContainerText}>
                      {formattedAppointment}
                    </Typography>
                  )
                : appointmentDate != null &&
                  appointmentTime != null && (
                    <Typography sx={topPatientInfoContainerText}>
                      {appointmentDate} @ {formattedTime}
                    </Typography>
                  )}
            </Box>
            <Box sx={patientInfoContainers}>
              <Box sx={bottomPatientInfoContainerText}>
                DOB: {formattedDate} (Age {age})
              </Box>
              <Box sx={bottomPatientInfoContainerText}>
                Chart #: {chartNumber || "-"}
              </Box>
              {status && totalForms > 0 && (
                <Box sx={statusContainer}>
                  <Box sx={dotBox} component="span">
                    {status === "Completed" ? (
                      <img src={GreenDot} alt="Completed" />
                    ) : (
                      <img src={OrangeDot} alt="Incomplete" />
                    )}
                  </Box>
                  <Box component="span" sx={bottomPatientInfoContainerText}>
                    {status}
                  </Box>
                  <Box
                    component="span"
                    sx={bottomPatientInfoContainerTextLight}
                  >
                    {` (${packageName}: ${completedForms}/${totalForms} completed)`}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={npContainer}>
            {np && !noBorder && <Box sx={npText}>NP</Box>}
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};
